import React, { ChangeEvent, ReactElement, useCallback } from 'react';
import { useRecoilState } from 'recoil';
import { FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, SxProps, Typography, TypographyProps } from '@mui/material';

import { Field } from '../../../../../../lib/recoil/Form';

export type Value = string | boolean | null;

export interface SingleChoiceQuestionProps<T extends Value> {
    title: string;
    data: Record<string, string>;
    field: Field<T>;
    disabled?: boolean;
    error?: boolean;
    radioColor?: any;
    titleProps?: TypographyProps;
    getFormControlLabelSxProps?: (error: boolean, disabled: boolean, selected: boolean) => SxProps;
}

export const  SingleChoiceQuestion = <T extends Value>({
  title,
  data,
  field,
  disabled = false,
  error = false,
  radioColor = 'default',
  titleProps,
  getFormControlLabelSxProps,
}: SingleChoiceQuestionProps<T>): ReactElement  => {
  const [ value, setValue ] = useRecoilState(field.valueState);

  /**
  * This function handles the change event for the input element.
  * It's wrapped in a useCallback to avoid unnecessary re-renders.
  *
  * @param {ChangeEvent<HTMLInputElement>} event - The event object from the input change.
  */
  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
  // Get the value from the event target (the input element)
    const inputValue = event.target.value;

    // If the inputValue is 'true' or 'false', convert it to a boolean.
    // Otherwise, leave it as is.
    const newValue = inputValue === 'true' ? true : inputValue === 'false' ? false : inputValue;

    setValue(newValue as T);
  }, [setValue]);

  return (
    <FormControl error={error}>
      <FormLabel focused={false}>
        <Typography variant='h3' {...titleProps}>{title}</Typography>
      </FormLabel>
      <RadioGroup onChange={handleChange} value={value}>
        {
          Object.entries(data).map(([ key, label ]) =>  {
            const selected = `${value}` === `${key}`;

            return (
              <FormControlLabel
                control={
                  (
                    <Radio
                      color={error ? 'error' : radioColor}
                      disableRipple
                      disabled={disabled}
                      style={{ backgroundColor: 'transparent' }}
                    />
                  )
                }
                key={key}
                label={label}
                sx={getFormControlLabelSxProps ? getFormControlLabelSxProps(error, disabled, selected) : undefined}
                value={key}
              />
            );
          },
          )
        }
      </RadioGroup>
    </FormControl>
  );
};

export default SingleChoiceQuestion;
